import {Component, inject, input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ResearchPaperDTO, ResearchSource} from '../../dto/research-paper';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatChipsModule} from '@angular/material/chips';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatDialog} from '@angular/material/dialog';
import {
  ScreeningCriteriasResultsDialogComponent
} from '../screening-criterias-results-dialog/screening-criterias-results-dialog.component';
import {UploadDialogComponent} from '../../../../utils/components/upload-dialog/upload-dialog.component';
import {
  ResearchPaperUpdateDialogComponent
} from '../research-paper-update-dialog/research-paper-update-dialog.component';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {paperScreeningResult} from '../../helper/research-paper';
import {Store} from '@ngrx/store';
import {uploadResearchPaperPDF} from '../../services/research-paper/research-paper.actions';
import {environment} from '../../../../../environments/environment';
import {MatTooltip} from '@angular/material/tooltip';
import {RouterLink} from '@angular/router';

@Component({
  selector: 'app-research-paper-card',
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltip,
    RouterLink
  ],
  templateUrl: './research-paper-card.component.html',
  styleUrl: './research-paper-card.component.scss',
  animations: [
    trigger('paperChange', [
      state('initial', style({
        transform: 'scale(1)',
        opacity: 1,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      })),
      state('changed', style({
        transform: 'scale(1)',
        opacity: 1,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      })),
      transition('initial => changed', [
        style({
          transform: 'scale(1.1)',
          opacity: 0.5,
          boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)',
        }),
        animate('0.4s ease-out', style({
          transform: 'scale(0.9)',
          opacity: 0.8,
        })),
        animate('0.3s ease-in', style({
          transform: 'scale(1) rotate(0)',
          opacity: 1,
        })),
      ]),
      transition('changed => initial', [
        style({
          transform: 'scale(1.1)',
          opacity: 0.5,
          boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)',
        }),
        animate('0.4s ease-out', style({
          transform: 'scale(0.9)',
          opacity: 0.8,
        })),
        animate('0.3s ease-in', style({
          transform: 'scale(1)',
          opacity: 1,
        })),
      ]),
    ]),
  ]
})
export class ResearchPaperCardComponent implements OnChanges, OnInit {
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly store: Store = inject(Store);
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  readonly paper = input.required<ResearchPaperDTO>();
  readonly disablePapersDialog = input<boolean>(false);
  readonly editable = input<boolean>(false);
  readonly linkSelectedTab = input<number>(0);
  readonly disableAnimation = input<boolean>(false);
  readonly hideAuthor = input<boolean>(false);
  readonly hideChips = input<boolean>(false);

  animationState = 'initial';

  isSmallScreen = false;

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.Handset])
      .subscribe(result => {
        this.isSmallScreen = result.matches;
      });

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.disableAnimation()) {
      return;
    }
    if (!changes['paper']) {
      return;
    }
    if (!changes['paper'].isFirstChange()) {
      const prevPaper = changes['paper'].previousValue;
      const currentPaper = changes['paper'].currentValue;
      if (JSON.stringify(prevPaper) !== JSON.stringify(currentPaper)) {
        this.animationState = 'changed';
        setTimeout(() => this.animationState = 'initial', 500);
      }
    }
  }

  viewResults(screeningOne: boolean): void {
    if (!this.paper().latestScreeningRun) {
      return;
    }
    if (screeningOne && !this.paper().latestScreeningRun!.screeningOne) {
      return;
    }
    if (!screeningOne && !this.paper().latestScreeningRun!.screeningTwo) {
      return;
    }

    const runId = screeningOne ? this.paper().latestScreeningRun!.screeningOne!.id : this.paper().latestScreeningRun!.screeningTwo!.id;
    if (!runId) {
      return;
    }
    const results = screeningOne ? paperScreeningResult(this.paper()) : paperScreeningResult(this.paper(), true);
    if (results.length === 0) {
      return;
    }
    this.dialog.open(ScreeningCriteriasResultsDialogComponent, {
      data: {
        result: results,
        runId: runId!,
        paper: this.paper(),
        editable: this.editable(),
        isScreeningOne: screeningOne
      }
    });
  }

  uploadFile(): void {
    if (!this.editable()) {
      return;
    }
    if (!this.paper().projectId || !this.paper().id) {
      return;
    }
    const dialogRef = this.dialog.open(UploadDialogComponent, {
      width: '400px',
    });

    dialogRef.afterClosed().subscribe((result?: File) => {
      if (result) {
        this.store.dispatch(uploadResearchPaperPDF({
          projectId: this.paper().projectId,
          id: this.paper().id,
          file: result,
        }))
      }
    });
  }


  openUpdateDialog(): void {
    if (!this.isSelfCreated()) {
      return;
    }
    if (!this.editable()) {
      return;
    }
    this.dialog.open(ResearchPaperUpdateDialogComponent, {
      width: '600px',
      height: '80vh',
      maxWidth: '100vw',
      autoFocus: false,
      data: this.paper(),
    });
  }

  isSelfCreated(): boolean {
    return (this.paper().source === ResearchSource.SELF_CREATED);
  }

  getPDFUrl(): string | undefined {
    const url = this.paper().pdfUrl;
    if (!url) {
      return undefined;
    }
    if (url.startsWith('http')) {
      return url;
    }
    return environment.apiBaseUrl + url;
  }

  hasNoPdfDownloaded(): boolean {
    if (this.paper().pdfUrl) {
      return true;
    }
    if (!this.paper().hasDownloadedPdf) {
      return false;
    }
    return this.paper().hasDownloadedPdf!;
  }

}
